import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PUBLIC_ROUTES } from "@/routes/public";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * JUB-105: point-of-collection GDPR notice linked from the RSVP and gift
 * contribution consent checkboxes. Deliberately narrower than a full site
 * Privacy Policy (tracked separately in JUB-100, blocked on owner-approved
 * legal text) — this page only covers the two public forms that collect
 * personal data today, using factual, generic language, mirroring
 * CookiesPolicyPage. Shares the public header/footer and page meta with the
 * blog and FAQ pages (JUB-180).
 */
export default function DataProcessingNoticePage() {
  const { t } = useTranslation();
  const locale = useRouteLocale();

  usePageMeta({
    title: t("dataNotice.metaTitle"),
    description: t("dataNotice.metaDescription"),
    locale,
    path: PUBLIC_ROUTES.dataProcessingNotice(locale),
    alternates: { ro: PUBLIC_ROUTES.dataProcessingNotice("ro"), en: PUBLIC_ROUTES.dataProcessingNotice("en") },
  });

  return (
    <PublicContentLayout>
      <Card>
        <CardHeader>
          <CardTitle className="text-3xl">
            {t("dataNotice.title")}
          </CardTitle>
          <CardDescription>
            {t("dataNotice.lastUpdated")}
          </CardDescription>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none dark:prose-invert">
          <section className="mb-6">
            <p>{t("dataNotice.intro")}</p>
          </section>

          <section className="mb-6">
            <h2 className="text-2xl font-semibold mb-3">
              {t("dataNotice.rsvp.title")}
            </h2>
            <p>{t("dataNotice.rsvp.content")}</p>
          </section>

          <section className="mb-6">
            <h2 className="text-2xl font-semibold mb-3">
              {t("dataNotice.gift.title")}
            </h2>
            <p>{t("dataNotice.gift.content")}</p>
          </section>

          <section className="mb-6">
            <h2 className="text-2xl font-semibold mb-3">
              {t("dataNotice.retention.title")}
            </h2>
            <p>{t("dataNotice.retention.content")}</p>
          </section>

          <section className="mb-6">
            <h2 className="text-2xl font-semibold mb-3">
              {t("dataNotice.rights.title")}
            </h2>
            <p>{t("dataNotice.rights.content")}</p>
          </section>

          <section className="mb-6">
            <h2 className="text-2xl font-semibold mb-3">
              {t("dataNotice.contact.title")}
            </h2>
            <p>{t("dataNotice.contact.content")}</p>
          </section>
        </CardContent>
      </Card>
    </PublicContentLayout>
  );
}
